/**
 * PaymentTrackingTab Component
 * 
 * This component provides the interface for payment analytics and tracking.
 * It displays advanced payment reporting features and launches the analytics modal.
 * 
 * Features:
 * - Advanced aging analysis with trend forecasting
 * - Comprehensive payment analytics and KPI dashboards
 * - Collection insights and performance metrics
 * - Full internationalization support (Arabic/English)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ChartBarIcon, 
  DocumentChartBarIcon, 
  ClockIcon 
} from '@heroicons/react/24/outline';
import PaymentTrackingModal from '../PaymentTrackingModal';

const PaymentTrackingTab = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const handleSuccess = () => {
    setShowModal(false);
    // Optionally refresh data
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t('payment_analytics.title', 'Payment Analytics')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('payment_analytics.description', 'Advanced payment reporting, aging analysis, and collection insights')}
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
        >
          <ChartBarIcon className="h-4 w-4" />
          <span>{t('payment_analytics.open_analytics', 'Open Payment Analytics')}</span>
        </button>
      </div>

      {/* Feature Description */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-green-900 mb-3">
          {t('payment_analytics.features', 'Payment Analytics Features')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <ChartBarIcon className="h-6 w-6 text-green-600" />
            </div>
            <h4 className="font-medium text-green-900 mb-2">{t('payment_analytics.aging_analysis', 'Aging Analysis')}</h4>
            <p className="text-green-700 text-sm">{t('payment_analytics.aging_analysis_desc', 'Advanced aging reports with trend analysis and forecasting')}</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <DocumentChartBarIcon className="h-6 w-6 text-green-600" />
            </div>
            <h4 className="font-medium text-green-900 mb-2">{t('payment_analytics.payment_reports', 'Payment Reports')}</h4>
            <p className="text-green-700 text-sm">{t('payment_analytics.payment_reports_desc', 'Comprehensive payment analytics and KPI dashboards')}</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <ClockIcon className="h-6 w-6 text-green-600" />
            </div>
            <h4 className="font-medium text-green-900 mb-2">{t('payment_analytics.collection_insights', 'Collection Insights')}</h4>
            <p className="text-green-700 text-sm">{t('payment_analytics.collection_insights_desc', 'Advanced collection analytics and performance metrics')}</p>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <PaymentTrackingModal
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default PaymentTrackingTab;
